import { Badge } from '@internal/ui-kit/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@internal/ui-kit/components/ui/table'
import { AssignInventoryDrawer } from './AssignInventoryDrawer'
import { EditInventoryDrawer } from './EditInventoryDrawer'
import { useInventories, useSlimProducts } from './useUserProfileData'

/**
 * The combined page's "Inventory" tab (HATCH-101 round 4 — split out of the
 * original combined "Loyalty & insurance" tab into its own tab per ticket
 * feedback, plus `assignInventory` wired up — deferred in the original PR,
 * now requested: "need an ability to assign inventory as in retool").
 * `getInventories` — `table1`. Round 5 feedback: `product_ids` are now
 * resolved against `useSlimProducts` (the same real `slim-products` resource
 * `AssignInventoryDrawer`'s own product picker already uses) and shown as
 * title badges instead of raw ids.
 */
export function InventoryTab({ userId }: { userId: string }) {
  const { inventories, isPending, refetch } = useInventories(userId)
  const { products } = useSlimProducts()

  const productTitle = (productId: string) => products.find((product) => product.id === productId)?.title ?? productId

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Inventory</h4>
        <AssignInventoryDrawer userId={userId} onCreated={refetch} />
      </div>
      {isPending ? (
        <p className="text-muted-foreground text-sm">Loading inventory…</p>
      ) : inventories.length === 0 ? (
        <p className="text-muted-foreground text-sm">No inventory items.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Products</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead>Original quantity</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {inventories.map((inventory) => (
              <TableRow key={inventory.inventory_id}>
                <TableCell>
                  {(inventory.product_ids ?? []).length === 0 ? (
                    '—'
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {(inventory.product_ids ?? []).map((productId) => (
                        <Badge key={productId} variant="secondary">
                          {productTitle(productId)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>{inventory.quantity ?? '—'}</TableCell>
                <TableCell>{inventory.original_quantity ?? '—'}</TableCell>
                <TableCell>{(inventory.tags ?? []).join(', ') || '—'}</TableCell>
                <TableCell>
                  <EditInventoryDrawer userId={userId} inventory={inventory} onUpdated={refetch} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
